import { Helmet } from "react-helmet-async";

const BASE_URL = "https://ahmadrajput.com";
const DEFAULT_TITLE = "Ahmad Rajput | AI Automation Engineer";
const DEFAULT_DESCRIPTION =
  "AI automation engineer building multi-agent workflows, RAG systems, document intelligence, CRM integrations, cloud scrapers, Frappe/ERPNext apps, and production backend automation.";
const DEFAULT_IMAGE = "/profile.png";
const DEFAULT_KEYWORDS = [
  "Ahmad Rajput",
  "AI automation engineer",
  "n8n automation",
  "RAG engineer",
  "multi-agent systems",
  "workflow automation",
  "CRM integrations",
  "FastAPI",
  "pgvector",
  "document intelligence",
  "Frappe",
  "ERPNext",
  "Docker",
  "AWS Lambda",
  "MCP tools"
];

const absoluteUrl = (value = "") => {
  if (!value) return BASE_URL;
  if (/^https?:\/\//i.test(value)) return value;
  return `${BASE_URL}${value.startsWith("/") ? value : `/${value}`}`;
};

const SEO = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url = "/",
  keywords = DEFAULT_KEYWORDS,
  type = "website"
}) => {
  const canonicalUrl = absoluteUrl(url);
  const imageUrl = absoluteUrl(image);
  const socialProfiles = [
    "https://www.linkedin.com/in/ahmad-bin-khalil",
    "https://github.com/AhmadBinKhalil"
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Person",
      name: "Ahmad Rajput",
      alternateName: "Ahmad Bin Khalil",
      url: BASE_URL,
      image: imageUrl,
      sameAs: socialProfiles,
      jobTitle: "AI Automation Engineer",
      description,
      knowsAbout: [
        "Workflow Automation",
        "AI Agents",
        "RAG Systems",
        "System Integration",
        "Cloud Automation",
        "Document Intelligence",
        "CRM Automation",
        "Backend Engineering",
        "Frappe ERPNext Development",
        "n8n Automation"
      ],
      hasOccupation: {
        "@type": "Occupation",
        name: "AI Automation Engineer",
        occupationalCategory: "Software Engineering"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Ahmad Rajput Portfolio",
      url: BASE_URL,
      description,
      inLanguage: "en",
      publisher: {
        "@type": "Person",
        name: "Ahmad Rajput"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "Ahmad Rajput AI Automation Engineering",
      url: BASE_URL,
      image: imageUrl,
      description,
      areaServed: "Worldwide",
      serviceType: [
        "AI Automation",
        "Workflow Automation",
        "RAG Pipeline Engineering",
        "CRM Integration",
        "Frappe ERPNext Development",
        "Backend Automation"
      ],
      founder: {
        "@type": "Person",
        name: "Ahmad Rajput"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${BASE_URL}/#home`
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "About",
          item: `${BASE_URL}/#about`
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Projects",
          item: `${BASE_URL}/#projects`
        },
        {
          "@type": "ListItem",
          position: 4,
          name: "Contact",
          item: `${BASE_URL}/#contact`
        }
      ]
    }
  ];

  return (
    <Helmet htmlAttributes={{ lang: "en" }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(", ")} />
      <meta name="author" content="Ahmad Rajput" />
      <meta name="creator" content="Ahmad Rajput" />
      <meta name="publisher" content="Ahmad Rajput" />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="referrer" content="strict-origin-when-cross-origin" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#4F5D75" />
      <meta name="application-name" content="Ahmad Rajput" />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="Ahmad Rajput" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content="Ahmad Rajput - AI Automation Engineer" />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:url" content={canonicalUrl} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content="Ahmad Rajput - AI Automation Engineer" />

      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
